import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  TextField, 
  Autocomplete, 
  InputAdornment, 
  CircularProgress,
  IconButton,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { Search, Close, MyLocation } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import type { Map as LeafletMap, LatLngBoundsExpression } from 'leaflet';
import { LeafletMouseEvent } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useDebounce } from '../../../hooks/useDebounce';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

type LatLon = { lat: number; lon: number };
type LatLngPair = { latitude: number; longitude: number };
type AddressOption = any;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (lat: number, lon: number, address?: string) => void;
  initialCoordinates?: LatLon | LatLngPair;
};

const brazilBounds: LatLngBoundsExpression = [[-34, -75], [6, -34]];
const brazilCenter = { lat: -14.235, lon: -51.925 };
const isWithinBrazil = (lat: number, lon: number) => lat >= -34 && lat <= 6 && lon >= -75 && lon <= -34;

const normalizeCoords = (input?: LatLon | LatLngPair | null): LatLon | null => {
  if (!input) return null;
  if ('lat' in input && 'lon' in input) return { lat: input.lat, lon: input.lon };
  if ('latitude' in input && 'longitude' in input) return { lat: input.latitude, lon: input.longitude };
  return null;
};

const LocationSelector: React.FC<{ onSelect: (lat: number, lon: number) => void }> = ({ onSelect }) => {
  useMapEvents({
    click(e: LeafletMouseEvent) {
      onSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
};

const MapInvalidator: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);
  return null;
};

const MapRefSetter: React.FC<{ onInit: (map: LeafletMap) => void }> = ({ onInit }) => {
  const map = useMap();
  useEffect(() => {
    onInit(map);
  }, [map, onInit]);
  return null;
};

const CoordinateSelectorDialog: React.FC<Props> = ({ open, onClose, onConfirm, initialCoordinates }) => {
  const [coords, setCoords] = useState<LatLon | null>(() => {
    const n = normalizeCoords(initialCoordinates ?? null);
    if (n && n.lat === 0 && n.lon === 0) return null;
    return n;
  });
  const [map, setMap] = useState<LeafletMap | null>(null);
  
  // Address Search State
  const [addressQuery, setAddressQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const debouncedAddressQuery = useDebounce(addressQuery, 500);

  const [latInput, setLatInput] = useState('');
  const [lonInput, setLonInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [streetInput, setStreetInput] = useState('');
  const [numberInput, setNumberInput] = useState('');
  const [complementInput, setComplementInput] = useState('');
  const [neighborhoodInput, setNeighborhoodInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [stateInput, setStateInput] = useState('');

  // Sync coords with initialCoordinates when dialog opens
  useEffect(() => {
    if (open) {
      const n = normalizeCoords(initialCoordinates ?? null);
      if (n && (n.lat !== 0 || n.lon !== 0)) {
        setCoords(n);
        setLatInput(n.lat.toString());
        setLonInput(n.lon.toString());
      } else {
        setCoords(null);
        setLatInput('');
        setLonInput('');
      }
      setStreetInput('');
      setNumberInput('');
      setComplementInput('');
      setNeighborhoodInput('');
      setCityInput('');
      setStateInput('');
      setAddressQuery('');
      setAddressOptions([]);
      setInputError(null);
      setActiveTab(0);
    }
  }, [open, initialCoordinates]);

  // Sync inputs when coords change (e.g. from map click)
  useEffect(() => {
    if (coords) {
      setLatInput(coords.lat.toFixed(6));
      setLonInput(coords.lon.toFixed(6));
    }
  }, [coords]);

  // Search address using Nominatim (and ViaCEP for Brazil)
  useEffect(() => {
    let active = true;

    if (debouncedAddressQuery === '') {
      setAddressOptions([]);
      return undefined;
    }

    const fetchAddress = async () => {
      setLoadingAddress(true);
      try {
        const cepDigits = debouncedAddressQuery.replace(/\D/g, '');
        let foundViaCep = false;

        // Try ViaCEP if it looks like a Brazilian CEP (8 digits)
        if (cepDigits.length === 8) {
          try {
            const response = await fetch(`https://viacep.com.br/ws/${encodeURIComponent(cepDigits)}/json/`, {
              headers: {
                'User-Agent': 'MuuApp/1.0',
              },
            });
            const cepData = await response.json();
            
            if (!active) return;
            
            if (!cepData.erro) {
              foundViaCep = true;
              const street = cepData.logradouro || '';
              const city = cepData.localidade || '';
              const state = cepData.uf || '';

              setStreetInput(street);
              setNeighborhoodInput(cepData.bairro || '');
              setCityInput(city);
              setStateInput(state);
              setComplementInput(cepData.complemento || '');
              setInputError(null);

              const geocodeUrl = `https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(
                street
              )}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&country=Brazil&addressdetails=1&limit=5&accept-language=pt-BR`;

              const geoResponse = await fetch(geocodeUrl, {
                headers: {
                  'User-Agent': 'MuuApp/1.0',
                },
              });
              const data = await geoResponse.json();
              if (active) {
                const list = data || [];
                setAddressOptions(list);
                const first = list?.[0];
                if (first) {
                  const foundLat = parseFloat(first.lat);
                  const foundLon = parseFloat(first.lon);
                  setCoords({ lat: foundLat, lon: foundLon });
                  setLatInput(foundLat.toString());
                  setLonInput(foundLon.toString());
                  map?.flyTo([foundLat, foundLon], 15, { duration: 1.5 });
                }
              }
            }
          } catch (err) {
            // Ignore ViaCEP error and fall through to global search
          }
        }

        // Fallback: Global search via Nominatim
        if (!foundViaCep && active) {
          const geocodeUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            debouncedAddressQuery
          )}&addressdetails=1&limit=5&accept-language=pt-BR`;

          const geoResponse = await fetch(geocodeUrl, {
            headers: {
              'User-Agent': 'MuuApp/1.0',
            },
          });
          const data = await geoResponse.json();
          
          if (active) {
            setAddressOptions(data || []);
          }
        }

      } catch (err) {
        console.error('Error fetching address:', err);
      } finally {
        if (active) {
          setLoadingAddress(false);
        }
      }
    };

    fetchAddress();

    return () => {
      active = false;
    };
  }, [debouncedAddressQuery]);

  const handleAddressSelect = (event: any, newValue: AddressOption) => {
    if (!newValue || typeof newValue === 'string') return;

    const address = newValue.address || {};
    setStreetInput(address.road || '');
    setNeighborhoodInput(address.suburb || address.neighbourhood || '');
    setCityInput(address.city || address.town || address.village || '');
    setStateInput(address.state || '');
    setInputError(null);

    const lat = parseFloat(newValue.lat);
    const lon = parseFloat(newValue.lon);

    setCoords({ lat, lon });
    setLatInput(lat.toString());
    setLonInput(lon.toString());
    map?.flyTo([lat, lon], 15, { duration: 1.5 });
  };

  const handleCoordinatesApply = () => {
    const latStr = latInput.trim().replace(',', '.');
    const lonStr = lonInput.trim().replace(',', '.');
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);

    if (isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
      setCoords({ lat, lon });
      setInputError(null);
      map?.flyTo([lat, lon], 15, { duration: 1.5 });
      return;
    }

    setInputError('Preencha latitude e longitude válidas');
  };

  const handleAddressApply = async () => {
    const street = streetInput.trim();
    const city = cityInput.trim();
    const state = stateInput.trim();
    if (!street || !city || !state) {
      setInputError('Preencha rua, cidade e UF para localizar no mapa');
      return;
    }

    setLoadingAddress(true);
    setInputError(null);
    try {
      const geocodeUrl = `https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(
        [street, numberInput.trim()].filter(Boolean).join(' ')
      )}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&country=Brazil&addressdetails=1&limit=1&accept-language=pt-BR`;
      const response = await fetch(geocodeUrl, {
        headers: {
          'User-Agent': 'MuuApp/1.0',
        },
      });
      const data = await response.json();
      const first = data?.[0];
      if (!first) {
        setInputError('Endereço não encontrado');
        return;
      }
      const foundLat = parseFloat(first.lat);
      const foundLon = parseFloat(first.lon);
      setCoords({ lat: foundLat, lon: foundLon });
      setLatInput(foundLat.toString());
      setLonInput(foundLon.toString());
      map?.flyTo([foundLat, foundLon], 15, { duration: 1.5 });
    } catch (error) {
      setInputError('Falha ao buscar endereço');
    } finally {
      setLoadingAddress(false);
    }
  };

  // Fit to Brazil on open if no coords
  useEffect(() => {
    if (open && map) {
      if (coords) {
        map.setView([coords.lat, coords.lon], 15);
      } else {
        map.fitBounds(brazilBounds, { animate: false, padding: [16, 16] });
      }
      setTimeout(() => map.invalidateSize(), 200);
    }
  }, [open, map]); // Removed coords from dependency to avoid resetting view on every click

  const handleConfirm = () => {
    if (coords) {
      // Find the address string if possible, or just pass coords
      // Here we assume the parent will handle reverse geocoding if needed, 
      // or we could pass the last selected address name if we tracked it.
      // For now, just passing lat/lon as per interface.
      onConfirm(coords.lat, coords.lon);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md" 
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: 2, 
        borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` 
      }}>
        <DialogTitle sx={{ p: 0, fontWeight: 600 }}>Informe sua localização</DialogTitle>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setInputError(null);
          }}
          variant="fullWidth"
          sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Por Geolocalização" />
          <Tab label="Por Endereço" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1.5, alignItems: 'start' }}>
            <TextField
              label="Latitude"
              placeholder="-00.000000"
              value={latInput}
              onChange={(e) => {
                setLatInput(e.target.value);
                setInputError(null);
              }}
              error={!!inputError}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><MyLocation fontSize="small" /></InputAdornment>
              }}
            />
            <TextField
              label="Longitude"
              placeholder="-00.000000"
              value={lonInput}
              onChange={(e) => {
                setLonInput(e.target.value);
                setInputError(null);
              }}
              error={!!inputError}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><MyLocation fontSize="small" /></InputAdornment>
              }}
            />
            <Button
              variant="outlined"
              onClick={handleCoordinatesApply}
              sx={{
                height: 40,
                color: DESIGN_TOKENS.colors.organization.farm,
                borderColor: DESIGN_TOKENS.colors.organization.farm,
                '&:hover': {
                  borderColor: DESIGN_TOKENS.colors.organization.farm,
                  backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08`,
                }
              }}
            >
              Ir
            </Button>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              freeSolo
              open={false}
              options={addressOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                const cepRaw = option.address?.postcode || '';
                const cepDigits = cepRaw.replace(/\D/g, '').slice(0, 8);
                const cep = cepDigits.length === 8 ? `${cepDigits.slice(0, 5)}-${cepDigits.slice(5)}` : cepRaw;
                const city = option.address?.city || option.address?.town || option.address?.village || '';
                const state = option.address?.state || '';
                const cityState = city && state ? `${city} - ${state}` : (city || state);
                return [cep, cityState].filter(Boolean).join(' • ') || option.display_name;
              }}
              renderOption={(props, option) => {
                const cepRaw = option.address?.postcode || '';
                const cepDigits = cepRaw.replace(/\D/g, '').slice(0, 8);
                const cep = cepDigits.length === 8 ? `${cepDigits.slice(0, 5)}-${cepDigits.slice(5)}` : cepRaw;
                const city = option.address?.city || option.address?.town || option.address?.village || '';
                const state = option.address?.state || '';
                const road = option.address?.road || '';
                const suburb = option.address?.suburb || option.address?.neighbourhood || '';
                const secondary = [road, suburb].filter(Boolean).join(', ');
                const title = [cep, city && state ? `${city} - ${state}` : (city || state)].filter(Boolean).join(' • ');

                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" fontWeight={500}>
                        {title || option.display_name.split(',')[0]}
                      </Typography>
                      <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                        {secondary}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              filterOptions={(x) => x}
              loading={loadingAddress}
              onChange={handleAddressSelect}
              onInputChange={(_, newInputValue) => {
                setAddressQuery(newInputValue);
              }}
              ListboxProps={{
                style: {
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  padding: '8px',
                  marginTop: '8px'
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar CEP"
                  fullWidth
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: params.InputProps.endAdornment,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm },
                      '&:hover fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
                    }
                  }}
                />
              )}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5 }}>
                <TextField
                  label="Rua"
                  placeholder="Ex: Av. Brasil"
                  value={streetInput}
                  onChange={(e) => {
                    setStreetInput(e.target.value);
                    setInputError(null);
                  }}
                  size="small"
                />
                <TextField
                  label="Número"
                  placeholder="Ex: 123"
                  value={numberInput}
                  onChange={(e) => {
                    setNumberInput(e.target.value);
                    setInputError(null);
                  }}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField
                  label="Complemento"
                  placeholder="Ex: Apto 12"
                  value={complementInput}
                  onChange={(e) => {
                    setComplementInput(e.target.value);
                    setInputError(null);
                  }}
                  size="small"
                />
                <TextField
                  label="Bairro"
                  placeholder="Ex: Centro"
                  value={neighborhoodInput}
                  onChange={(e) => {
                    setNeighborhoodInput(e.target.value);
                    setInputError(null);
                  }}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5 }}>
                <TextField
                  label="Cidade"
                  placeholder="Ex: São Carlos"
                  value={cityInput}
                  onChange={(e) => {
                    setCityInput(e.target.value);
                    setInputError(null);
                  }}
                  size="small"
                />
                <TextField
                  label="UF"
                  placeholder="Ex: SP"
                  value={stateInput}
                  onChange={(e) => {
                    setStateInput(e.target.value.toUpperCase());
                    setInputError(null);
                  }}
                  inputProps={{ maxLength: 2 }}
                  size="small"
                />
              </Box>
              <Button
                variant="outlined"
                onClick={handleAddressApply}
                sx={{
                  height: 40,
                  color: DESIGN_TOKENS.colors.organization.farm,
                  borderColor: DESIGN_TOKENS.colors.organization.farm,
                  '&:hover': {
                    borderColor: DESIGN_TOKENS.colors.organization.farm,
                    backgroundColor: `${DESIGN_TOKENS.colors.organization.farm}08`,
                  }
                }}
              >
                Buscar endereço
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
            Você também pode selecionar o ponto clicando diretamente no mapa.
          </Typography>
        </Box>
        {inputError && (
          <Typography variant="caption" color="error" sx={{ mt: -1 }}>
            {inputError}
          </Typography>
        )}

        <Box sx={{ 
          height: { xs: 350, sm: 450 }, 
          width: '100%', 
          borderRadius: 2, 
          overflow: 'hidden', 
          border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
          position: 'relative'
        }}>
          <MapContainer
            center={[coords?.lat ?? brazilCenter.lat, coords?.lon ?? brazilCenter.lon]}
            zoom={4}
            minZoom={3}
            style={{ height: '100%', width: '100%' }}
          >
            <MapRefSetter onInit={(m) => setMap(m)} />
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            <LocationSelector onSelect={(lat, lon) => setCoords({ lat, lon })} />
            {coords && <Marker position={[coords.lat, coords.lon]} />}
            <MapInvalidator />
          </MapContainer>
          
          {/* Coordinates Overlay */}
          {coords && (
            <Box sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              right: 16,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              p: 1.5,
              borderRadius: 2,
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Latitude</Typography>
                <Typography variant="body2" fontWeight={500}>{coords.lat.toFixed(6)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Longitude</Typography>
                <Typography variant="body2" fontWeight={500}>{coords.lon.toFixed(6)}</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!coords}
          variant="contained"
          sx={{
            bgcolor: DESIGN_TOKENS.colors.organization.farm,
            '&:hover': {
              bgcolor: DESIGN_TOKENS.colors.organization.farm, // Darken slightly if possible
              filter: 'brightness(0.9)'
            }
          }}
        >
          Confirmar Localização
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoordinateSelectorDialog;
